import { useCallback, useEffect, useRef, useState } from "react";

/**
 * Debounced autosave for an edit-in-place surface (frontend-doctrine §4.13).
 *
 * WHY A TIMER IS ALLOWED HERE. §4.13 forbids `setInterval`-polling a vault
 * surface to fake reactivity — that rule governs the READ path, where a poll
 * would paper over the absence of a subscription. This is the write path: one
 * `setTimeout` armed per edit burst and cleared on the next keystroke, so it
 * fires once when typing PAUSES and never on a schedule of its own. Idle costs
 * nothing; there is no background loop.
 *
 * The save it performs MUST be body-only — writing to the path the note already
 * lives at, never deriving a new one. A path-deriving save renames (write-new +
 * delete-old) and moves the selection, and doing that on a typing pause churns
 * files and remounts the editor mid-keystroke. See `notebook.tsx:handleAutosave`.
 */

/** Idle time before a paused edit is written. Long enough not to fire between
 *  words, short enough to land inside a few seconds of thinking. */
export const AUTOSAVE_DELAY_MS = 1000;

export type AutosaveStatus = "idle" | "pending" | "saving" | "saved" | "error";

interface UseDebouncedAutosaveArgs<T> {
  /** The live edit value. A change from the last SAVED value arms the timer. */
  value: T;
  /** Persist `value`. Resolves true on success. Must not rename or move. */
  onSave: (value: T) => Promise<boolean>;
  /** Off while the surface can't legally save (read mode, unresolved load). */
  enabled?: boolean;
  delayMs?: number;
}

interface UseDebouncedAutosave {
  status: AutosaveStatus;
  /** Write NOW if dirty, cancelling any armed timer. Resolves when the write
   *  settles, so a caller can await it before switching notes or unmounting. */
  flush: () => Promise<void>;
  /** True when the live value differs from what was last persisted. */
  isDirty: boolean;
}

export function useDebouncedAutosave<T>({
  value,
  onSave,
  enabled = true,
  delayMs = AUTOSAVE_DELAY_MS,
}: UseDebouncedAutosaveArgs<T>): UseDebouncedAutosave {
  const [status, setStatus] = useState<AutosaveStatus>("idle");
  const [isDirty, setIsDirty] = useState(false);

  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  /** The last value we successfully persisted — the dirty comparison basis.
   *  Seeded with the mount value so merely opening a note is never "dirty". */
  const savedValueRef = useRef<T>(value);
  const valueRef = useRef<T>(value);
  /** In-flight guard. Two writes to one note would race to last-write-wins on
   *  an ordering the network picks, so a save armed mid-flight waits its turn. */
  const inFlightRef = useRef(false);
  const rerunRef = useRef(false);
  /** Held in a ref so re-arming never depends on the caller memoizing onSave. */
  const onSaveRef = useRef(onSave);
  onSaveRef.current = onSave;

  const clearTimer = useCallback(() => {
    if (timerRef.current !== null) {
      clearTimeout(timerRef.current);
      timerRef.current = null;
    }
  }, []);

  const runSave = useCallback(async (): Promise<void> => {
    const pending = valueRef.current;
    if (Object.is(pending, savedValueRef.current)) return;
    if (inFlightRef.current) {
      // Coalesce: whatever is newest when the current write lands gets written.
      rerunRef.current = true;
      return;
    }
    inFlightRef.current = true;
    setStatus("saving");
    try {
      const ok = await onSaveRef.current(pending);
      if (ok) {
        savedValueRef.current = pending;
        setIsDirty(!Object.is(valueRef.current, pending));
        setStatus("saved");
      } else {
        setStatus("error");
      }
    } catch {
      // The parent owns the display copy (it holds the NotebookError); this
      // hook only reports that the write did not land.
      setStatus("error");
    } finally {
      inFlightRef.current = false;
      if (rerunRef.current) {
        rerunRef.current = false;
        void runSave();
      }
    }
  }, []);

  // Arm on change. Cleared and re-armed by the next keystroke, so the write
  // happens on the PAUSE, not on a fixed cadence.
  useEffect(() => {
    valueRef.current = value;
    const dirty = !Object.is(value, savedValueRef.current);
    setIsDirty(dirty);
    if (!enabled || !dirty) return;
    setStatus("pending");
    clearTimer();
    timerRef.current = setTimeout(() => {
      timerRef.current = null;
      void runSave();
    }, delayMs);
    return clearTimer;
  }, [value, enabled, delayMs, clearTimer, runSave]);

  const flush = useCallback(async (): Promise<void> => {
    clearTimer();
    await runSave();
  }, [clearTimer, runSave]);

  // Flush on unmount so an in-SPA navigation inside the debounce window keeps
  // the work. Ref-read (not `value`) so the effect never re-runs on keystrokes.
  useEffect(() => {
    return () => {
      clearTimer();
      if (!Object.is(valueRef.current, savedValueRef.current)) void runSave();
    };
  }, [clearTimer, runSave]);

  // Tab close / reload. A write cannot be awaited here — browsers ignore async
  // work in `beforeunload` — so this PROMPTS rather than pretending to save.
  useEffect(() => {
    if (!enabled) return;
    function onBeforeUnload(e: BeforeUnloadEvent) {
      if (Object.is(valueRef.current, savedValueRef.current)) return;
      e.preventDefault();
    }
    window.addEventListener("beforeunload", onBeforeUnload);
    return () => window.removeEventListener("beforeunload", onBeforeUnload);
  }, [enabled]);

  return { status, flush, isDirty };
}
